import React from "react";
import { renderToStaticMarkup } from "react-dom/server";

const PAPER_WIDTH = "80mm";

const WIDTHS = {
  "58mm": { px: 220, fontSize: 11, totalSize: 15, smallSize: 9 },
  "80mm": { px: 302, fontSize: 12, totalSize: 17, smallSize: 10 },
};

const BARS = [2, 1, 3, 1, 2, 1, 2, 4, 1, 2, 1, 3, 1, 2, 1, 4, 1, 2, 1, 3, 1, 2, 4, 1, 2, 1, 3, 2, 1, 4, 1, 2, 1, 3, 2];
const HEIGHTS = [28, 20, 28, 16, 28, 24, 28, 28, 18, 28, 26, 28, 22, 28, 18, 28, 24, 28, 20, 28, 26, 28, 28, 22, 28, 18, 28, 28, 24, 28, 20, 28, 16, 28, 28];

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toMoney = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? `Q${parsed.toFixed(2)}` : "Q0.00";
};

const formatDateTime = (value) => {
  const candidate = value ? new Date(value) : new Date();
  const date = Number.isNaN(candidate.getTime()) ? new Date() : candidate;
  return date.toLocaleString("es-GT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const toQuantityLabel = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  if (Number.isInteger(parsed)) return String(parsed);
  return parsed.toFixed(2).replace(/\.?0+$/, "");
};

export function SaleTicket({ sale }) {
  if (!sale) return null;

  const cfg = WIDTHS[PAPER_WIDTH] ?? WIDTHS["80mm"];
  const pad = PAPER_WIDTH === "58mm" ? "8px 10px" : "10px 14px";
  const items = toArray(sale.items);
  const saleDateTime = formatDateTime(sale?.paid_at || sale?.created_at);
  const customerLabel = String(sale?.customer_display || "CF").trim() || "CF";
  const saleTypeLabel = String(sale?.sale_type || "").toUpperCase() === "GROOMING" ? "Grooming" : "Producto";
  const petLabel = String(sale?.grooming_pet_name || "").trim();
  const paymentLabel =
    String(sale?.primary_payment_label || "").trim() ||
    String(sale?.payments?.[0]?.payment_method_name || sale?.payments?.[0]?.payment_method_code || "-").trim() ||
    "-";

  return (
    <>
      <style>{`
        @media print {
          body > * { display: none !important; }
          .thermal-ticket-print { display: block !important; }
          .thermal-ticket-print * { display: revert !important; }
          body { margin: 0 !important; background: #fff !important; }
        }
      `}</style>

      <div
        className="thermal-ticket-print"
        style={{
          width: cfg.px,
          background: "#fff",
          fontFamily: "'Courier Prime', 'Courier New', monospace",
          fontSize: cfg.fontSize,
          color: "#111",
        }}
      >
        <div style={{ padding: pad }}>
          <div style={{ textAlign: "center", marginBottom: 6 }}>
            <div style={{ fontWeight: 700, letterSpacing: "0.04em" }}>Pawsi Patitas</div>
            <div style={{ fontSize: cfg.smallSize }}>{saleDateTime}</div>
            <div style={{ fontSize: cfg.smallSize }}>Tipo: {saleTypeLabel}</div>
            <div style={{ fontSize: cfg.smallSize }}>Cliente: {customerLabel}</div>
            {petLabel ? <div style={{ fontSize: cfg.smallSize }}>Mascota: {petLabel}</div> : null}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.7fr 0.9fr 0.9fr 1fr",
              columnGap: 6,
              fontSize: cfg.smallSize - 1,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              borderBottom: "1px dashed #999",
              paddingBottom: 4,
            }}
          >
            <div>Descripcion</div>
            <div style={{ textAlign: "right" }}>Precio</div>
            <div style={{ textAlign: "right" }}>Descuento</div>
            <div style={{ textAlign: "right" }}>Subtotal</div>
          </div>

          <div style={{ marginTop: 4 }}>
            {items.length === 0 ? (
              <div style={{ textAlign: "center", fontSize: cfg.smallSize, color: "#666", padding: "6px 0" }}>
                Sin items
              </div>
            ) : (
              items.map((item, index) => (
                <div key={item.id ?? index} style={{ borderBottom: index < items.length - 1 ? "1px dashed #ddd" : "none", padding: "4px 0" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.7fr 0.9fr 0.9fr 1fr",
                      columnGap: 6,
                      alignItems: "start",
                    }}
                  >
                    <div style={{ lineHeight: 1.3 }}>
                      <div style={{ fontWeight: 700, wordBreak: "break-word" }}>
                        {`${toQuantityLabel(item.qty_presentation)} x ${item.presentation_name ? `${item.presentation_name} - ` : ""}${item.product_description || "-"}`}
                      </div>
                      <div style={{ fontSize: cfg.smallSize - 1, color: "#444" }}>
                        Codigo: {item.product_code || "-"}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>{toMoney(item.unit_price)}</div>
                    <div style={{ textAlign: "right" }}>{toMoney(item.discount_amount)}</div>
                    <div style={{ textAlign: "right", fontWeight: 700 }}>{toMoney(item.line_total)}</div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ borderTop: "1px dashed #999", marginTop: 4, paddingTop: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "1px 0" }}>
              <span>Metodo</span>
              <span>{paymentLabel}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "1px 0" }}>
              <span>Subtotal</span>
              <span>{toMoney(sale.subtotal ?? sale.total)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "1px 0" }}>
              <span>Descuento</span>
              <span>{toMoney(sale.discount_total ?? 0)}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontWeight: 700,
                fontSize: cfg.totalSize,
                borderTop: "2px solid #111",
                marginTop: 4,
                paddingTop: 4,
              }}
            >
              <span>Total</span>
              <span>{toMoney(sale.total)}</span>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-end",
              gap: 1.2,
              height: cfg.totalSize === 17 ? 32 : 28,
              margin: "10px 0 6px",
            }}
          >
            {BARS.map((width, index) => (
              <div
                key={`${width}-${index}`}
                style={{
                  width,
                  height: HEIGHTS[index % HEIGHTS.length],
                  background: "#111",
                  borderRadius: 0.5,
                }}
              />
            ))}
          </div>

          <div style={{ textAlign: "center", marginTop: 4 }}>Gracias por su compra</div>
        </div>
      </div>
    </>
  );
}

export function buildSaleTicketDocument({ sale, branchName, cashierName }) {
  if (!sale) return "";
  const markup = renderToStaticMarkup(
    <SaleTicket sale={sale} branchName={branchName} cashierName={cashierName} />
  );
  return `<!doctype html>
  <html lang="es">
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>${sale.sale_number || "Venta"}</title>
      <style>
        body {
          margin: 0;
          padding: 16px;
          background: #fff;
          display: flex;
          justify-content: center;
        }
      </style>
    </head>
    <body>${markup}</body>
  </html>`;
}

export function printSaleTicket({ sale, branchName, cashierName, ticketWindow = null }) {
  if (!sale || typeof window === "undefined") return;
  const nextWindow = ticketWindow || window.open("", "_blank", "width=420,height=760");
  if (!nextWindow) return;
  nextWindow.document.open();
  nextWindow.document.write(
    buildSaleTicketDocument({ sale, branchName, cashierName })
  );
  nextWindow.document.close();
  nextWindow.focus();
  window.setTimeout(() => {
    nextWindow.print();
  }, 150);
}

export default SaleTicket;
